import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import songService from '../../services/songService';
import serviceService from '../../services/serviceService';
import ViewSongPart from '../View/components/ViewSongPart';
import { transposeSong, getKeyAfterTranspose } from '../../utils/autoTranspose';
import { computePartNumbers } from '../../utils/partNumbers';
import { getAttachedNotes, isAttachedNote } from '../../utils/notes';
import { isHiddenInOverview } from '../../utils/parts';
import { setDocTitle } from '../../utils/utilities';
import { applyColorTheme, clearColorTheme } from '../../utils/colorTheme';
import GlobalLoader from '../../components/common/GlobalLoader/GlobalLoader';
import PageBar from '../../components/PageBar';
import { formatDate } from '../../utils/format';

import { ReactComponent as SquareIcon } from '../../icons/square-rounded.svg';
import { ReactComponent as CheckCircleIcon } from '../../icons/check-circle-solid.svg';
import { ReactComponent as PrintIcon } from '../../icons/printer.svg';

import './PrintService.scss';

// The pure core of View's applyAutoTranspose — a song stored with a transposeBy offset prints in
// the key it is actually played in. View's version also tracks the in-session "Original Key" reset,
// which has no meaning on a printed sheet.
const applyPrintTranspose = (song) => {
  const semitones = typeof song.transposeBy === 'string' ? parseInt(song.transposeBy, 10) : song.transposeBy;

  if (!semitones || isNaN(semitones)) return song;

  const originalKey = song.key;
  const transposed = transposeSong(song, semitones);

  transposed.key = getKeyAfterTranspose(originalKey, semitones);
  transposed.isAutoTransposed = true;
  transposed.originalKey = originalKey;
  transposed.autoTransposeSemitones = semitones;

  return transposed;
};

const formatLongDate = (date) =>
  formatDate(date, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

const formatShortDate = (date) => formatDate(date, { month: 'short', day: 'numeric' });

// Roughly how tall a part will render. Chords sit on their own lines inside the lyrics, so the
// newline count already accounts for them; the extra two cover the part chip and the blank line
// that follows every part.
const estimatePartHeight = (part) => (part.lyrics || '').split(/\r\n|\r|\n/).length + 2;

const splitIntoColumns = (parts) => {
  const heights = parts.map(estimatePartHeight);
  const halfway = heights.reduce((total, height) => total + height, 0) / 2;

  let running = 0;
  let splitAt = parts.length;

  for (let i = 0; i < parts.length; i += 1) {
    running += heights[i];

    if (running >= halfway) {
      splitAt = i + 1;
      break;
    }
  }

  return [parts.slice(0, splitAt), parts.slice(splitAt)];
};

const PrintService = () => {
  const { t } = useTranslation();
  const params = useParams();
  const history = useHistory();

  const [service, setService] = useState(null);
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(false);
  const [phone, setPhone] = useState(false);

  useLayoutEffect(() => {
    const wasDark = document.body.classList.contains('dark');

    if (wasDark) document.body.classList.replace('dark', 'light');

    clearColorTheme();

    document.body.classList.add('print-sheet');

    return () => {
      if (wasDark) document.body.classList.replace('light', 'dark');

      applyColorTheme();
      document.body.classList.remove('print-sheet');
    };
  }, []);

  useEffect(() => {
    const fetchService = async () => {
      try {
        const { data } = await serviceService.getService(params.id);

        const loadedSongs = await Promise.all(
          data.songsIds.map(async (songId) => {
            const song = await songService.getSong(songId);
            song.parts = songService.loadDuplicateParts(song);
            return applyPrintTranspose(song);
          }),
        );

        setService(data);
        setSongs(loadedSongs);
        setLoading(false);
      } catch (ex) {
        setLoading(false);
        if (ex.response && (ex.response.status === 404 || ex.response.status === 400)) {
          history.replace('/not-found');
        }
      }
    };

    fetchService();
  }, [params.id, history]);

  // Chrome and Safari seed the Save-as-PDF filename from document.title, so it leads with the
  // service and the app name trails behind it, courtesy of setDocTitle.
  useEffect(() => {
    if (!service) return undefined;

    const previousTitle = document.title;
    setDocTitle(`${service.title} - ${formatShortDate(service.date)}`);

    return () => {
      document.title = previousTitle;
    };
  }, [service]);

  // What a song can print, worked out once: the full sheet, and the overview skeleton taken from it.
  // The menu's counter and the sheet have to agree on what counts as a part, so both read this.
  const printableParts = useMemo(
    () =>
      songs.map((song) => {
        // Drop attached notes (they ride inside their host) and admin-only hidden parts, before the column split.
        const full = song.parts.filter((part) => !part.isHidden && !isAttachedNote(part, song.parts));

        // Same predicate the player's overview mode uses, so a part that disappears on screen is
        // the same part that disappears on paper — mostly duplicates, which is where the pages go.
        return { full, overview: full.filter((part) => !isHiddenInOverview(part)) };
      }),
    [songs],
  );

  const partTotals = useMemo(
    () =>
      printableParts.reduce(
        (totals, song) => ({
          full: totals.full + song.full.length,
          overview: totals.overview + song.overview.length,
        }),
        { full: 0, overview: 0 },
      ),
    [printableParts],
  );

  // The app's own splash covers the load rather than a line of text. It stays mounted once the
  // songs land so its fade-out can play, uncovering a sheet that is already drawn underneath.
  return (
    <>
      <GlobalLoader show={loading} />
      {service && (
        <>
          <PageBar
            back={{ label: t('common.back'), icon: true }}
            title={t('common.print')}
            actions={[{ label: t('common.print'), icon: <PrintIcon />, onClick: () => window.print() }]}
            chips={[
              {
                label: (
                  <>
                    {t('view.overview')}
                    <span className="print-overview-count">
                      {partTotals.overview}/{partTotals.full}
                    </span>
                  </>
                ),
                icon: overview ? <CheckCircleIcon /> : <SquareIcon />,
                iconClassName: overview ? 'solid' : '',
                pressed: overview,
                onClick: () => setOverview(!overview),
              },
              {
                label: t('printService.phoneSize'),
                icon: phone ? <CheckCircleIcon /> : <SquareIcon />,
                iconClassName: phone ? 'solid' : '',
                pressed: phone,
                onClick: () => setPhone(!phone),
              },
            ]}
          />
          {/* @page can't be scoped to a class, so the phone page size only exists while it's on.
              Rendered after the app's stylesheets, it overrides the A4 rule in PrintService.scss. */}
          {phone && <style>{'@media print { @page { size: 100mm 210mm; margin: 6mm; } }'}</style>}
          <div className={phone ? 'PrintService phone' : 'PrintService'}>
            <header className="header">
              <p className="date">{formatLongDate(service.date)}</p>
              <h1 className="title">{service.title}</h1>
              {/* The sheet has to say it is abridged. On screen the toggle shows its own state, but
                  the paper outlives it, and a chart that quietly omits parts is worse than no chart. */}
              <p className="count">
                {t('printService.songCount', { count: songs.length })}
                {overview && ` · ${t('printService.overviewNote')}`}
              </p>
            </header>

            {/* .song-chart carries the part colours, chord styling and pre-wrap that the player gets
                from #view-body (see ViewBody.scss) */}
            <div className="song-chart">
              {songs.map((song, index) => {
                // Numbered from the song's own parts, not the printed subset, so "Verse 2" is still the
                // second verse on an overview sheet even when Verse 1's repeat is not printed.
                const partNumbers = computePartNumbers(song.parts);
                const visibleParts = overview ? printableParts[index].overview : printableParts[index].full;
                const columns = phone ? [visibleParts] : splitIntoColumns(visibleParts);

                const renderPart = (part) => (
                  <ViewSongPart
                    key={part.id}
                    part={part}
                    displayNumber={partNumbers[part.id]}
                    songTeamId={song.team?._id}
                    songId={song._id}
                    songKey={song.key}
                    songOriginalKey={song.originalKey ?? song.key}
                    songTimeSignature={song.timeSignature}
                    transposeSemitones={song.autoTransposeSemitones ?? 0}
                    attachedNotes={getAttachedNotes(song.parts, part.id).filter((note) => !note.isHidden)}
                    disablePartOptions
                  />
                );

                return (
                  <section key={song._id} className="song">
                    <div className="song-header">
                      <h2 className="song-title">
                        <span className="song-number">{index + 1}</span>
                        {song.title}
                      </h2>
                      <p className="song-meta">
                        {[
                          song.artist,
                          song.key && t('dialogs.chordBars.key', { key: song.key }),
                          song.bpm && t('printService.bpm', { bpm: song.bpm }),
                          song.timeSignature,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                    </div>

                    <div className="song-parts">
                      {columns.map((columnParts, columnIndex) => (
                        <div key={columnIndex} className="song-column">
                          {columnParts.map(renderPart)}
                        </div>
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default PrintService;
